import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const BMRCalculatorPage = () => {
  const [unitSystem, setUnitSystem] = useState('us');
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('25');
  const [weightLbs, setWeightLbs] = useState('160');
  const [weightKg, setWeightKg] = useState('73');
  const [heightFeet, setHeightFeet] = useState('5');
  const [heightInches, setHeightInches] = useState('10');
  const [heightCm, setHeightCm] = useState('178');
  const [result, setResult] = useState(null);

  const calculate = () => {
    let heightCmVal, weightKgVal;

    if (unitSystem === 'us') {
      heightCmVal = (parseFloat(heightFeet) * 12 + parseFloat(heightInches)) * 2.54;
      weightKgVal = parseFloat(weightLbs) * 0.453592;
    } else {
      heightCmVal = parseFloat(heightCm);
      weightKgVal = parseFloat(weightKg);
    }
    const ageVal = parseFloat(age);

    // Mifflin-St Jeor Equation
    let bmrMifflin;
    if (gender === 'male') {
      bmrMifflin = 10 * weightKgVal + 6.25 * heightCmVal - 5 * ageVal + 5;
    } else {
      bmrMifflin = 10 * weightKgVal + 6.25 * heightCmVal - 5 * ageVal - 161;
    }

    // Harris-Benedict Equation
    let bmrHarris;
    if (gender === 'male') {
      bmrHarris = 88.362 + 13.397 * weightKgVal + 4.799 * heightCmVal - 5.677 * ageVal;
    } else {
      bmrHarris = 447.593 + 9.247 * weightKgVal + 3.098 * heightCmVal - 4.330 * ageVal;
    }

    // Katch-McArdle (assumes 20% body fat if not provided)
    const leanMass = weightKgVal * 0.80;
    const bmrKatch = 370 + 21.6 * leanMass;

    setResult({
      mifflin: Math.round(bmrMifflin),
      harris: Math.round(bmrHarris),
      katch: Math.round(bmrKatch),
      average: Math.round((bmrMifflin + bmrHarris + bmrKatch) / 3),
      activities: [
        { name: 'Sedentary (little or no exercise)', multiplier: 1.2, calories: Math.round(bmrMifflin * 1.2) },
        { name: 'Light (exercise 1-3 days/week)', multiplier: 1.375, calories: Math.round(bmrMifflin * 1.375) },
        { name: 'Moderate (exercise 3-5 days/week)', multiplier: 1.55, calories: Math.round(bmrMifflin * 1.55) },
        { name: 'Active (exercise 6-7 days/week)', multiplier: 1.725, calories: Math.round(bmrMifflin * 1.725) },
        { name: 'Very Active (hard exercise daily)', multiplier: 1.9, calories: Math.round(bmrMifflin * 1.9) }
      ]
    });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">BMR Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">BMR Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate your Basal Metabolic Rate - the number of calories your body burns at rest.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button onClick={() => setUnitSystem('us')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${unitSystem === 'us' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                US Units
              </button>
              <button onClick={() => setUnitSystem('metric')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${unitSystem === 'metric' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                Metric Units
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="text-gray-300 w-20">Gender</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={gender === 'male'} onChange={() => setGender('male')} className="accent-cyan-500" />
                    <span className="text-gray-300">Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={gender === 'female'} onChange={() => setGender('female')} className="accent-cyan-500" />
                    <span className="text-gray-300">Female</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="text-gray-300 w-20">Age</label>
                <Input type="number" value={age} onChange={(e) => setAge(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                <span className="text-gray-400">years</span>
              </div>
              {unitSystem === 'us' ? (
                <>
                  <div className="flex items-center gap-4">
                    <label className="text-gray-300 w-20">Height</label>
                    <Input type="number" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)}
                      className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">ft</span>
                    <Input type="number" value={heightInches} onChange={(e) => setHeightInches(e.target.value)}
                      className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">in</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="text-gray-300 w-20">Weight</label>
                    <Input type="number" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">lbs</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <label className="text-gray-300 w-20">Height</label>
                    <Input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">cm</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="text-gray-300 w-20">Weight</label>
                    <Input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">kg</span>
                  </div>
                </>
              )}
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">Your BMR (Mifflin-St Jeor)</p>
                  <p className="text-4xl font-bold text-green-400">{result.mifflin.toLocaleString()}</p>
                  <p className="text-gray-500">Calories/day</p>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Mifflin-St Jeor</p>
                    <p className="text-white font-bold">{result.mifflin.toLocaleString()} cal</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Harris-Benedict</p>
                    <p className="text-white font-bold">{result.harris.toLocaleString()} cal</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Katch-McArdle</p>
                    <p className="text-white font-bold">{result.katch.toLocaleString()} cal</p>
                  </div>
                </div>

                <h4 className="text-white font-semibold mb-3">Daily Calorie Needs by Activity Level</h4>
                <div className="space-y-2">
                  {result.activities.map((act, i) => (
                    <div key={i} className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">{act.name}</span>
                      <span className="text-white font-semibold">{act.calories.toLocaleString()} cal</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness & Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'BMI', path: '/bmi-calculator' }, { name: 'Calorie', path: '/calorie-calculator' }, { name: 'Body Fat', path: '/body-fat-calculator' }, { name: 'Ideal Weight', path: '/ideal-weight-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BMRCalculatorPage;
